import { useId, type SelectHTMLAttributes } from 'react';
import { cn } from './cn.ts';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  /**
   * Heading this option sits under.
   *
   * Only meaningful when one control offers choices of several kinds —
   * an invoice's `Add from`, which draws on change orders, estimates and
   * bills at once. Options keep the order they are given; a run of
   * options sharing a group becomes one `<optgroup>`, which is what tells
   * a screen reader "change order" before reading out a number. Options
   * with no group render loose, so an ungrouped list is unchanged.
   */
  readonly group?: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly error?: string;
  /** Explains a choice whose consequence is not obvious from the options. */
  readonly hint?: string;
}

export function Select({ label, options, error, hint, className, ...rest }: SelectProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-700">
        {label}
      </label>
      <select
        {...rest}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm',
          'focus:outline focus:outline-2 focus:outline-blue-500',
          className,
        )}
      >
        {groupRuns(options).map((run) =>
          run.group === undefined ? (
            run.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))
          ) : (
            <optgroup key={`${run.group}-${run.options[0]?.value ?? ''}`} label={run.group}>
              {run.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ),
        )}
      </select>
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Consecutive options sharing a group, in the order they were given.
 *
 * Runs rather than a sort, so a caller decides the order and this does not
 * quietly rearrange a list — the first option is often a "Choose one"
 * placeholder that has to stay first.
 */
function groupRuns(
  options: readonly SelectOption[],
): { group: string | undefined; options: SelectOption[] }[] {
  const runs: { group: string | undefined; options: SelectOption[] }[] = [];
  for (const option of options) {
    const last = runs.at(-1);
    if (last && last.group === option.group) last.options.push(option);
    else runs.push({ group: option.group, options: [option] });
  }
  return runs;
}
